import { normalizeLangKey } from '@src/utility/bcp47';
import { isOriginalLang } from '@src/utility/captionLanguage';

import type { KaraokeTextBranding } from '@src/types.ts/story';
import type { AspectRatio } from '@src/types.ts/video';

/**
 * PLAT-11416 — pure resolution helpers for the caption template family's
 * language dimension. A logical template is a family of
 * `karaoke_text_branding` records sharing a `presetId` (the family root's
 * own id doubles as the key — see KaraokeTemplateVariantService's
 * retroactive assignment), one record per aspect ratio and, now, per
 * language. An empty `language` means "all languages" (the pre-PLAT-11411
 * records).
 */

/**
 * A record's language as a canonical caption-language key, or null for the
 * language-neutral records (empty/absent `language`).
 */
export const normalizeCaptionVariantLanguage = (
  raw: string | null | undefined,
): string | null => {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  return normalizeLangKey(trimmed);
};

/**
 * The language key a config's caption-template variants should carry: the
 * canonical key for a real language, null for the original-language
 * sentinels — original-language variants stay language-neutral, exactly like
 * every pre-PLAT-11411 record.
 */
export const captionVariantLanguageForConfig = (
  configLanguage: string | null | undefined,
): string | null => {
  if (!configLanguage || isOriginalLang(configLanguage)) return null;
  return normalizeLangKey(configLanguage);
};

/**
 * Whether a record's `language` serves `languageKey`: exact canonical match,
 * or a base-subtag match when at least one side is the bare base — an
 * app-created variant carries the exact canonical key, but a hand-typed Dato
 * value like "ja" should still serve a "ja-JP" switch (and vice versa).
 * When BOTH sides carry subtags and they differ, they are distinct variants
 * — `zh-Hans` must never serve (or be overwritten by) a `zh-Hant` switch,
 * nor `pt-BR` a `pt-PT` one. Neutral records (no language) never match —
 * they are the fallback, not a variant.
 */
export const captionVariantMatchesLanguage = (
  recordLanguage: string | null | undefined,
  languageKey: string,
): boolean => {
  const recordKey = normalizeCaptionVariantLanguage(recordLanguage);
  if (!recordKey) return false;
  if (recordKey === languageKey) return true;
  const [recordBase, ...recordRest] = recordKey.split('-');
  const [targetBase, ...targetRest] = languageKey.split('-');
  if (recordBase !== targetBase) return false;
  return recordRest.length === 0 || targetRest.length === 0;
};

/**
 * The family member curated for `languageKey` at exactly `aspectRatio`, or
 * null. The family is anchored on the config's `parent_template_id`: the
 * parent's `presetId` when the parent record is known, else the id itself —
 * so variants resolve both under a family root and under a dangling or
 * global-preset parent id (org variants of a global preset store the
 * preset's display id as their `presetId`).
 */
export const findKaraokeLanguageVariant = (
  templates: readonly KaraokeTextBranding[] | null | undefined,
  parentTemplateId: string | null | undefined,
  languageKey: string,
  aspectRatio: AspectRatio,
): KaraokeTextBranding | null => {
  if (!parentTemplateId || !templates?.length) return null;
  const parent = templates.find(t => t.id === parentTemplateId);
  const familyKey = parent?.presetId || parentTemplateId;
  const family = templates.filter(
    t =>
      (t.presetId === familyKey || t.id === familyKey) &&
      t.aspectRatio === aspectRatio,
  );
  const exact = family.find(
    t => normalizeCaptionVariantLanguage(t.language) === languageKey,
  );
  if (exact) return exact;
  return (
    family.find(t => captionVariantMatchesLanguage(t.language, languageKey)) ??
    null
  );
};

/**
 * The family's language-neutral member at `aspectRatio` (the base look), or
 * null. Used to route BACK to the base when the current parent is a
 * language variant and the target language has no variant of its own —
 * without it the rebind would be one-way and the previous language's
 * styling would leak.
 */
export const findKaraokeNeutralFamilyMember = (
  templates: readonly KaraokeTextBranding[] | null | undefined,
  parentTemplateId: string | null | undefined,
  aspectRatio: AspectRatio,
): KaraokeTextBranding | null => {
  if (!parentTemplateId || !templates?.length) return null;
  const parent = templates.find(t => t.id === parentTemplateId);
  const familyKey = parent?.presetId || parentTemplateId;
  return (
    templates.find(
      t =>
        (t.presetId === familyKey || t.id === familyKey) &&
        t.aspectRatio === aspectRatio &&
        !t.language?.trim(),
    ) ?? null
  );
};

/**
 * Rank same-family candidates (already filtered to the target aspect ratio)
 * by language fit for `configLanguage`: a real language prefers its exact
 * variant and falls back to a language-neutral record; the original-language
 * sentinels prefer neutral and fall back to the legacy first-match, so
 * families with no tagged records behave exactly as before PLAT-11416.
 */
export const pickKaraokeVariantByLanguage = <
  T extends { language?: string | null },
>(
  candidates: readonly T[],
  configLanguage: string | null | undefined,
): T | null => {
  if (!candidates.length) return null;
  const targetKey = captionVariantLanguageForConfig(configLanguage);
  if (targetKey) {
    const exact = candidates.find(
      t => normalizeCaptionVariantLanguage(t.language) === targetKey,
    );
    if (exact) return exact;
  }
  const neutral = candidates.find(t => !t.language?.trim());
  return neutral ?? candidates[0];
};
